import { asString, nullishToValue } from '#library/coercion.library.js';
import { extract, ownEntries } from '#library/primitive.library.js';
import { stringify } from '#library/serialize.library.js';
import { isNumber, isDate, isObject, isDefined, isUndefined, isFunction, isCallable } from '#library/assertion.library.js';
// adapted from https://jsbin.com/insert/4/edit?js,output
/**
 * Inserts a value into an array at its sorted position using binary search.
 * Supports both primitive values and objects (via a sorting key).
 *
 * @param arr - The array to insert into (defaults to an empty array)
 * @param val - The value to insert
 * @param key - The property key to sort by if inserting objects
 * @returns The mutated array containing the inserted value
 * @example
 * ```ts
 * sortInsert([1, 3], 2); // [1, 2, 3]
 * ```
 */
export const sortInsert = (arr = [], val, key) => {
    const obj = isObject(val) && isDefined(key); // array of Objects
    let low = 0, high = arr.length;
    while (low < high) {
        const mid = (low + high) >>> 1; // divide by 2
        const source = obj
            ? arr[mid][key] // array of Object values
            : arr[mid]; // assume Primitive values
        const target = obj
            ? val[key]
            : val;
        if (source < target)
            low = mid + 1;
        else
            high = mid;
    }
    arr.splice(low, 0, val); // mutate original Array
    return arr;
};
/**
 * Creates a comparison function for sorting an array of objects by multiple keys.
 *
 * @param keys - The keys or sorting options to apply in priority order
 * @returns A comparison function suitable for `Array.prototype.sort()`
 * @example
 * ```ts
 * const arr = [{ a: 1, b: 'z' }, { a: 1, b: 'a' }];
 * arr.sort(sortBy('a', { field: 'b', dir: 'desc' }));
 * ```
 */
export function sortBy(...keys) {
    const sortOptions = keys // coerce string => SortBy
        .flat() // flatten Array-of-Array
        .map(key => isObject(key) ? key : { field: stringify(key) }); // build Array of sort-options
    return (left, right) => {
        let result = 0; // 0 = same, -1 = left<right, +1 = left>right
        sortOptions.forEach(key => {
            if (result === 0) { // no need to look further if result !== 0
                const dir = key.dir === 'desc' ? -1 : 1;
                const field = key.field + (isDefined(key.index) ? `[${key.index}]` : '');
                const valueA = extract(left, field, nullishToValue(key.default, 0));
                const valueB = extract(right, field, nullishToValue(key.default, 0));
                switch (true) {
                    case isNumber(valueA) && isNumber(valueB):
                    case isDate(valueA) && isDate(valueB):
                    case isObject(valueA) && isObject(valueB) && isNumber(valueA.valueOf()) && isNumber(valueB.valueOf()):
                        result = dir * (valueA - valueB);
                        break;
                    default:
                        result = dir * asString(valueA)?.localeCompare(asString(valueB));
                        break;
                }
            }
        });
        return result;
    };
}
/**
 * Sorts an array in place according to the specified keys or sorting options.
 *
 * @param array - The array to sort
 * @param keys - Sorting criteria, applied in order of priority
 * @returns The input array after sorting
 * @example
 * ```ts
 * const sorted = sortKey(users, 'lastName', 'firstName');
 * ```
 */
export function sortKey(array, ...keys) {
    return array.sort(sortBy(...keys));
}
/**
 * Internal implementation for grouping arrays.
 * Uses native Object.groupBy when available, otherwise provides a polyfill.
 * @internal
 */
const groupByImpl = (arr, fn) => {
    if (isCallable(Object.groupBy))
        return Object.groupBy(arr, fn);
    const res = Object.create(null);
    for (let idx = 0; idx < arr.length; idx++) {
        const itm = arr[idx];
        const key = fn(itm, idx);
        (res[key] ??= []).push(itm);
    }
    return res;
};
export function byKey(arr, fnKey, ...keys) {
    if (isFunction(fnKey))
        return groupByImpl(arr, fnKey);
    const keyed = [fnKey] // mapFn is a keyof T
        .concat(keys) // append any trailing keyof T[]
        .flat(); // flatten Array-of-Array
    return groupByImpl(arr, (itm) => // group an array into an object with named keys
     keyed
        .map(key => isUndefined(itm[key]) ? '' : stringify(itm[key]))
        .join('.'));
}
export function byLkp(arr, fnKey, ...keys) {
    const group = isFunction(fnKey)
        ? byKey(arr, fnKey) // group by the callback function
        : byKey(arr, fnKey, ...keys); // group by the list of keys
    return ownEntries(group)
        .reduce((acc, [key, grp]) => Object.assign(acc, { [key]: grp?.pop() }), {});
}
/**
 * Clears an array in-place, removing all elements and filling with null before truncation.
 *
 * @param arr - The array to clear
 * @returns The cleared array
 * @example
 * ```ts
 * const arr = [1, 2, 3];
 * clear(arr); // arr is now []
 * ```
 */
export function clear(arr) {
    arr.fill(null).length = 0;
    return arr;
}
/**
 * Generates the cartesian product of multiple arrays.
 *
 * @param args - The arrays to combine
 * @returns An array of combinations (each combination is an array)
 * @example
 * ```ts
 * cartesian([1, 2], ['a', 'b']); // [[1, 'a'], [1, 'b'], [2, 'a'], [2, 'b']]
 * ```
 */
export function cartesian(...args) {
    if (args.length === 0)
        return [];
    if (args.length === 1)
        return (args[0] || []).map(x => [x]);
    const [a, b = [], ...c] = args;
    const cartFn = (a, b) => [].concat(...a.map(d => b.map(e => [].concat(d, e))));
    return b.length
        ? cartesian(cartFn(a, b), ...c)
        : (a || []);
}
/**
 * Executes a callback with the provided array and returns the array.
 * Useful for side-effects (e.g., logging) in the middle of a function chain.
 *
 * @param arr - The array to tap into
 * @param fn - The callback to execute
 * @returns The original array
 * @example
 * ```ts
 * tap([1, 2, 3], console.log).map(x => x * 2);
 * ```
 */
export function tap(arr, fn) {
    fn(arr);
    return arr;
}
